import { getTargetData } from './getTargetData';
import { DEPENDENCY_MATRIX_CELLS_LIMIT } from 'views/environmentals/viewDataLimits';
import { getSourceData } from './getSourceData';
import { getReferences } from './getReferences';
import {
  getSelectedReferenceTypeNameByComponentId,
  getSourceWorkspaceIds,
} from './referenceTypeNameByComponent';
import { getComponentItemData, getLegendReferenceTypes } from '../utils';
import { componentInterface } from '@ardoq/component-interface';
import { ViewError } from '@ardoq/graph';
import { ItemsType, nodeLimitError } from '@ardoq/error-info-box';
import { dispatchAction } from '@ardoq/rxbeach';
import { bypassLimitAction } from './actions';
import {
  APIComponentAttributes,
  APIConstraintEnforcementMode,
  APIReferenceType,
  ArdoqId,
} from '@ardoq/api-types';
import {
  ComponentItemDataById,
  DependencyMatrixViewSettings,
  DependencyMatrixViewModel,
} from '../types';
import { PermissionContext } from '@ardoq/access-control';
import { IntendedRefTypes } from '../../../utils/metamodelConstraints';
import type { I18nLocale } from '@ardoq/i18n';
import { getNodeLimitErrorLabels } from 'locales/getErrorInfoBoxLabels';
import { compareComponentsById } from '@ardoq/common-helpers';
import { referenceInterface } from '@ardoq/reference-interface';
import { workspaceInterface } from '@ardoq/workspace-interface';
import { ContextSort } from '@ardoq/data-model';
import { formatNumber, type Locale } from '@ardoq/locale';
import { getComponentByIdSortDependencies } from 'modelInterface/components/getComponentByIdSortDependencies';

const getSourceLimit = (targetCount: number, bypassLimit: boolean) =>
  bypassLimit
    ? Infinity
    : Math.floor(DEPENDENCY_MATRIX_CELLS_LIMIT / targetCount) || 1;

const getComponentItemDataById = (
  componentIds: ArdoqId[],
  type: 'SOURCE' | 'TARGET'
): ComponentItemDataById =>
  Object.fromEntries(
    componentIds.map(componentId => [
      componentId,
      getComponentItemData({
        componentId,
        type,
        representationData:
          componentInterface.getRepresentationData(componentId),
        cssClassNames: componentInterface.getCssClassNames(componentId),
        isComponent: componentInterface.isComponent(componentId),
        typeName: componentInterface.getTypeName(componentId),
        workspaceId: componentInterface.getWorkspaceId(componentId),
      }),
    ])
  );

type BuildViewModelArgs = {
  componentId: ArdoqId;
  workspaceId: ArdoqId;
  workspaceIds: ArdoqId[];
  workspaceNamesByWorkspaceId: Record<ArdoqId, string>;
  referenceTypes: APIReferenceType[];
  resolvedReferenceToWsId: ArdoqId;
  focusedComponentId: ArdoqId | null;
  viewSettings: DependencyMatrixViewSettings;
  bypassLimit: boolean;
  permissionContext: PermissionContext;
  constraintEnforcementMode: APIConstraintEnforcementMode;
  intendedRefTypes: IntendedRefTypes | null;
  i18nLocale: I18nLocale;
  sort: ContextSort;
  userLocale: Locale;
  childrenByParent: Map<ArdoqId, APIComponentAttributes[]>;
};

/** What `buildViewModel` can derive from the graph alone. */
export type BuiltDependencyMatrixViewModel = Omit<
  DependencyMatrixViewModel,
  | 'locale'
  | 'i18nLocale'
  | 'errorInfoBoxLabels'
  | 'sort'
  | 'userLocale'
  | 'isReadOnlyVisualization'
>;

export const buildViewModel = ({
  workspaceId,
  workspaceIds,
  workspaceNamesByWorkspaceId,
  referenceTypes,
  resolvedReferenceToWsId,
  componentId,
  focusedComponentId,
  viewSettings,
  bypassLimit,
  permissionContext,
  constraintEnforcementMode,
  intendedRefTypes,
  i18nLocale,
  sort,
  userLocale,
  childrenByParent,
}: BuildViewModelArgs): BuiltDependencyMatrixViewModel => {
  const { showOnlyConnectedComponents } = viewSettings;

  // target- and sourceData get all workspace-relevant data regardless of showOnlyConnectedComponents state.
  const targetData = getTargetData(resolvedReferenceToWsId);
  // need all source data to get all "connected components"
  const sourceLimit = showOnlyConnectedComponents
    ? Infinity
    : getSourceLimit(targetData.allComponentsCount, bypassLimit);
  const sourceData = getSourceData(
    componentId,
    workspaceId,
    childrenByParent,
    sourceLimit
  );

  // As the matrix is 2-dimensional, we need only refs which are relevant for the current target workspace
  const allTargetWorkspaceComponents = new Set(targetData.componentIds);
  const referencesMap = getReferences(sourceData, allTargetWorkspaceComponents);

  const targetDataToDisplay = { ...targetData };
  const sourceDataToDisplay = { ...sourceData };

  if (showOnlyConnectedComponents) {
    const connectedTargetComponents = new Set<string>();
    const connectedSourceComponents = new Set<string>();

    const allConnectedTargetComponents = new Set<string>();

    for (const sourceComponentId in referencesMap) {
      referencesMap[sourceComponentId].forEach(
        ({ componentId: referencedComponentId }) => {
          if (connectedTargetComponents.has(referencedComponentId)) {
            const shouldAddSource =
              bypassLimit ||
              (!connectedSourceComponents.has(sourceComponentId) &&
                (connectedSourceComponents.size + 1) *
                  connectedTargetComponents.size <=
                  DEPENDENCY_MATRIX_CELLS_LIMIT);

            // add next source, if the resulting matrix is within limit
            if (shouldAddSource) {
              connectedSourceComponents.add(sourceComponentId);
            }
          } else {
            const nextSourceCount =
              connectedSourceComponents.size +
              (connectedSourceComponents.has(sourceComponentId) ? 0 : 1);
            const nextTargetCount = connectedTargetComponents.size + 1;

            // add next source and target if the resulting matrix is within limit
            if (
              bypassLimit ||
              nextSourceCount * nextTargetCount <= DEPENDENCY_MATRIX_CELLS_LIMIT
            ) {
              connectedSourceComponents.add(sourceComponentId);
              connectedTargetComponents.add(referencedComponentId);
            }
          }

          // count distinct all connected target components
          allConnectedTargetComponents.add(referencedComponentId);
        }
      );
    }

    // Display only connected components
    const connectedTargetIds = Array.from(connectedTargetComponents);
    const sortDependencies = getComponentByIdSortDependencies({
      componentIds: connectedTargetIds,
      sort,
      userLocale,
    });
    targetDataToDisplay.componentIds = connectedTargetIds.sort((a, b) =>
      compareComponentsById(a, b, sortDependencies)
    );
    targetDataToDisplay.allComponentsCount = allConnectedTargetComponents.size;

    sourceDataToDisplay.componentIds = Array.from(connectedSourceComponents);
    sourceDataToDisplay.allComponentsCount = Object.keys(referencesMap).length;
  }

  const legendReferenceTypes = getLegendReferenceTypes(
    workspaceId,
    referencesMap
  );

  // if one of the matrix dimensions is initially absent, empty display is not a result of this viewModifier.
  // if the view modifier removes one of the dimensions - we should communicate "No Connected Components"
  const noConnectedComponents = Boolean(
    showOnlyConnectedComponents &&
    targetData.componentIds.length &&
    sourceData.componentIds.length &&
    (!targetDataToDisplay.componentIds.length ||
      !sourceDataToDisplay.componentIds.length)
  );

  const fullMatrixCellsCount =
    targetDataToDisplay.allComponentsCount *
    sourceDataToDisplay.allComponentsCount;
  const displayedItems =
    targetDataToDisplay.componentIds.length +
    sourceDataToDisplay.componentIds.length;
  const shouldLimitItems =
    fullMatrixCellsCount > DEPENDENCY_MATRIX_CELLS_LIMIT && !bypassLimit;
  const fullMatrixComponentsCount =
    targetDataToDisplay.allComponentsCount +
    sourceDataToDisplay.allComponentsCount;

  const errors: ViewError[] = shouldLimitItems
    ? [
        nodeLimitError({
          itemCount: fullMatrixComponentsCount,
          limit: displayedItems,
          itemsType: ItemsType.COMPONENTS,
          formatNumber: value => formatNumber(value, 'default', userLocale),
          onProceedAnyway: () => dispatchAction(bypassLimitAction()),
          ...getNodeLimitErrorLabels(i18nLocale),
        }),
      ]
    : [];

  const isViewEmpty =
    targetDataToDisplay.componentIds.length === 0 ||
    sourceDataToDisplay.componentIds.length === 0 ||
    noConnectedComponents;

  const sourceComponentItemDataById = getComponentItemDataById(
    sourceDataToDisplay.componentIds,
    'SOURCE'
  );
  const targetComponentItemDataById = getComponentItemDataById(
    targetDataToDisplay.componentIds,
    'TARGET'
  );

  const referenceCssClassNamesById: Record<ArdoqId, string> =
    Object.fromEntries(
      Object.values(referencesMap)
        .flat()
        .map(({ referenceId }) => [
          referenceId,
          referenceInterface.getCssClassNames(referenceId) || '',
        ])
    );

  const currentReferenceCreationType =
    viewSettings.referenceCreationType[workspaceId];
  const sourceWorkspaceIds = getSourceWorkspaceIds(sourceComponentItemDataById);
  const referenceTypeNameByWorkspaceId: Record<ArdoqId, string | null> =
    Object.fromEntries(
      sourceWorkspaceIds.map(sourceWorkspaceId => [
        sourceWorkspaceId,
        currentReferenceCreationType
          ? (workspaceInterface.getReferenceTypeById(
              sourceWorkspaceId,
              currentReferenceCreationType
            )?.name ?? null)
          : null,
      ])
    );
  const selectedReferenceTypeNameByComponentId =
    getSelectedReferenceTypeNameByComponentId(
      sourceComponentItemDataById,
      referenceTypeNameByWorkspaceId
    );

  return {
    workspaceId,
    workspaceIds,
    workspaceNamesByWorkspaceId,
    referenceTypes,
    componentId,
    focusedComponentId,
    viewSettings: {
      ...viewSettings,
      referenceToWsId: resolvedReferenceToWsId,
    },
    targetData: targetDataToDisplay,
    sourceData: sourceDataToDisplay,
    sourceComponentItemDataById,
    targetComponentItemDataById,
    references: referencesMap,
    referenceCssClassNamesById,
    selectedReferenceTypeNameByComponentId,
    legendReferenceTypes,
    noConnectedComponents,
    isViewEmpty,
    errors,
    bypassLimit,
    permissionContext,
    constraintEnforcementMode,
    intendedRefTypes,
    childrenByParent,
  };
};
